/**
 * TalentSettings — premium dark Account Settings hub at /talent/settings.
 * Visual language mirrors the Admin Command Center (slate-950 canvas,
 * glassy cards, gradient icon chips, cyan/teal/purple/gold accents).
 * Compliance section is data-driven via GET /talent/settings/summary.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SettingsShell from '../../components/settings/SettingsShell';
import LinkedAccountsPanel from '../../components/LinkedAccountsPanel';
import MyBookingLinkCard from '../../components/MyBookingLinkCard';
import { SecurityPanel } from '../../components/settings/SecurityPanel';
import {
  HubCard, ActionRow, StatusPill, SummaryChip, SkeletonRow, ErrorState,
} from '../../components/settings/HubKit';
import { api } from '../../utils/api';
import {
  User, CreditCard, BellRing, CalendarClock, Lock, ShieldCheck,
  Trash2, Link2, ImageIcon, Zap, FileText, BadgeCheck, IdCard, LifeBuoy, Palette, Ban,
} from 'lucide-react';
import ThemePicker from '../../components/settings/ThemePicker';

const SECTIONS = [
  { id: 'profile',        label: 'Profile',        icon: User },
  { id: 'booking-link',   label: 'Booking link',   icon: Link2 },
  { id: 'appearance',     label: 'Appearance',     icon: Palette },
  { id: 'payout',         label: 'Payouts',        icon: CreditCard },
  { id: 'notifications',  label: 'Notifications',  icon: BellRing },
  { id: 'availability',   label: 'Availability',   icon: CalendarClock },
  { id: 'security',       label: 'Security',       icon: Lock },
  { id: 'linked-accounts',label: 'Linked accounts',icon: Link2 },
  { id: 'compliance',     label: 'Compliance',     icon: ShieldCheck },
  { id: 'danger',         label: 'Danger zone',    icon: Trash2 },
];

const IDV_PILL = {
  verified:       { tone: 'emerald', label: 'Verified' },
  pending_review: { tone: 'amber',   label: 'Pending Review' },
  needs_action:   { tone: 'rose',    label: 'Needs Action' },
  not_started:    { tone: 'slate',   label: 'Not Started' },
};

const CompliancePanel = () => {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.get('/talent/settings/summary');
      setSummary(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <HubCard title="Compliance" sub="Verification and agreements required to work events." testid="talent-settings-compliance">
        <SkeletonRow testid="compliance-skeleton-1" />
        <SkeletonRow />
        <SkeletonRow />
        <SkeletonRow />
      </HubCard>
    );
  }
  if (error || !summary) {
    return (
      <HubCard title="Compliance" testid="talent-settings-compliance">
        <ErrorState message="We couldn't load your compliance status." onRetry={load} testid="compliance-error" />
      </HubCard>
    );
  }

  const idv = IDV_PILL[summary.idv?.status] || IDV_PILL.not_started;
  const contractsOk = (summary.contracts?.pending ?? 0) === 0 && (summary.contracts?.accepted ?? 0) > 0;
  const certsExpiring = summary.certifications?.expiring ?? 0;
  const certsApproved = summary.certifications?.approved ?? 0;
  const certsPending = summary.certifications?.pending ?? 0;

  return (
    <div className="space-y-4">
      {/* Compliance summary strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5" data-testid="compliance-summary">
        <SummaryChip ok={summary.idv?.status === 'verified'} label="Verified Identity" testid="summary-idv" />
        <SummaryChip ok={contractsOk} label="Contracts Signed" testid="summary-contracts" />
        <SummaryChip ok={certsExpiring === 0 && certsPending === 0} label="Certifications" testid="summary-certs" />
        <SummaryChip ok={!!summary.profile_ready} label="Profile Ready" testid="summary-profile" />
      </div>

      <HubCard
        title="Compliance"
        sub="Verification and agreements required to work events."
        testid="talent-settings-compliance"
      >
        <ActionRow
          icon={IdCard}
          accent="cyan"
          label="ID Verification"
          sub="Verify your identity to unlock bookings"
          to="/talent/id-verification"
          badge={<StatusPill tone={idv.tone} label={idv.label} testid="idv-status-pill" />}
          testid="talent-settings-idv"
        />
        <ActionRow
          icon={FileText}
          accent="purple"
          label="Contracts"
          sub={
            summary.contracts?.pending > 0
              ? `${summary.contracts.pending} pending signature${summary.contracts.pending > 1 ? 's' : ''} · ${summary.contracts.accepted} signed`
              : `${summary.contracts?.accepted ?? 0} signed`
          }
          to="/talent/contracts"
          badge={
            summary.contracts?.pending > 0
              ? <StatusPill tone="amber" label={`${summary.contracts.pending} Pending`} testid="contracts-status-pill" />
              : <StatusPill tone="emerald" label="All Signed" testid="contracts-status-pill" />
          }
          testid="talent-settings-contracts"
        />
        <ActionRow
          icon={BadgeCheck}
          accent="gold"
          label="Certifications"
          sub={
            certsExpiring > 0
              ? `${certsExpiring} expiring within 30 days — renew to keep booking`
              : certsPending > 0
                ? `${certsApproved} earned · ${certsPending} under review`
                : `${certsApproved} earned — training badges boost your booking rate`
          }
          to="/talent/certifications"
          badge={
            certsExpiring > 0
              ? <StatusPill tone="rose" label={`${certsExpiring} Expiring`} testid="certs-status-pill" />
              : certsPending > 0
                ? <StatusPill tone="amber" label={`${certsPending} In Review`} testid="certs-status-pill" />
                : <StatusPill tone="emerald" label={`${certsApproved} Earned`} testid="certs-status-pill" />
          }
          testid="talent-settings-certs"
        />
        <ActionRow
          icon={ShieldCheck}
          accent="teal"
          label="Contractor Agreement"
          sub={summary.icma?.version ? `Current version ${summary.icma.version}` : 'Independent Contractor Master Agreement'}
          to="/talent/contractor-agreement"
          badge={
            summary.icma?.accepted
              ? <StatusPill tone="emerald" label="Accepted" testid="icma-status-pill" />
              : <StatusPill tone="amber" label="Review Needed" testid="icma-status-pill" />
          }
          testid="talent-settings-icma"
        />
      </HubCard>
    </div>
  );
};

const TalentSettings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [active, setActive] = useState(searchParams.get('section') || 'profile');

  const handleSection = (id) => {
    setActive(id);
    const next = new URLSearchParams(searchParams);
    next.set('section', id);
    setSearchParams(next, { replace: true });
  };

  return (
    <SettingsShell
      role="talent"
      variant="dark"
      sections={SECTIONS}
      activeSection={active}
      onSectionChange={handleSection}
    >
      {active === 'profile' && (
        <HubCard title="Profile" sub="Your public presence for clients and agencies." testid="talent-settings-profile">
          <ActionRow icon={User} accent="cyan" label="Edit profile" sub="Photo, bio, contact info, measurements & skills" to="/talent/profile" testid="talent-settings-edit-profile" />
          <ActionRow icon={ImageIcon} accent="purple" label="Portfolio" sub="Showcase photos clients see when booking you" to="/talent/portfolio" testid="talent-settings-portfolio" />
        </HubCard>
      )}
      {active === 'appearance' && (
        <HubCard title="Appearance" sub="Choose how iEvntür looks for you — everywhere you sign in." testid="talent-settings-appearance">
          <ThemePicker />
        </HubCard>
      )}
      {active === 'booking-link' && (
        <HubCard
          title="Booking link & custom URL"
          sub="Personalize your public booking handle and manage how clients find and book you. Changes sync automatically to your Digital Business Card."
          testid="talent-settings-booking-link"
        >
          <MyBookingLinkCard defaultOpen />
        </HubCard>
      )}
      {active === 'payout' && (
        <HubCard title="Payouts & banking" sub="Where your earnings land and how fast." testid="talent-settings-payout">
          <ActionRow icon={CreditCard} accent="teal" label="Earnings & bank setup" sub="Stripe payout account, history, and statements" to="/talent/earnings" testid="talent-settings-earnings" />
          <ActionRow icon={Zap} accent="gold" label="Instant payout" sub="Get paid the same day for eligible earnings" to="/talent/instant-payout" testid="talent-settings-instant-payout" />
        </HubCard>
      )}
      {active === 'notifications' && (
        <HubCard title="Notifications" sub="Only hear about what matters to you." testid="talent-settings-notifications">
          <ActionRow icon={BellRing} accent="pink" label="Notification settings" sub="Choose which emails and texts you receive" to="/talent/notification-settings" testid="talent-settings-notification-settings" />
          <ActionRow icon={Ban} accent="rose" label="Blocked clients" sub="Stop invitations, emails, texts & gigs from clients you block" to="/talent/blocked-clients" testid="talent-settings-blocked-clients" />
        </HubCard>
      )}
      {active === 'availability' && (
        <HubCard title="Availability" sub="Only see gigs you can actually work." testid="talent-settings-availability">
          <ActionRow icon={CalendarClock} accent="cyan" label="Availability calendar" sub="Block out dates so you only see gigs you can work" to="/talent/availability" testid="talent-settings-availability-link" />
        </HubCard>
      )}
      {active === 'security' && <SecurityPanel />}
      {active === 'linked-accounts' && <LinkedAccountsPanel />}
      {active === 'compliance' && <CompliancePanel />}
      {active === 'danger' && (
        <HubCard title="Danger zone" sub="Irreversible account actions." testid="talent-settings-danger">
          <p className="text-sm text-slate-400">
            Deactivating your account removes you from discovery and cancels
            pending applications. Because of payout and contract records, our
            team handles this manually — usually within one business day.
          </p>
          <ActionRow icon={LifeBuoy} accent="rose" label="Request deactivation" sub="Open a support ticket and we'll take care of it" to="/talent/help" testid="talent-settings-deactivate" />
        </HubCard>
      )}
    </SettingsShell>
  );
};

export default TalentSettings;
